// @ts-nocheck
/**
 * 自動対戦ループ画面
 * 借金返済のための強制パック開封・売却処理UI
 */

import { economyAccount, economyRequest } from '../../../lib/economyClient';
import { applyEconomySnapshot } from '../../../lib/economyState';
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { openPack } from '../../systems/packSystem';
import { valueCalculator } from '../../systems/valueCalculator';
import { getRarityMultiplier } from '../../systems/raritySystem';
import { createInitialDebtState } from '../systems/debtManager';
import { SEIZURE_CONSTANTS } from '../systems/seizureManager';
import { useIsMobile } from '../../hooks/useMediaQuery';

const AutoBattleLoop = ({
  playerData,
  updatePlayerData,
  initialDebt,
  allCards,
  cardValueMap,
  processDayAdvancement,
  onComplete,
}) => {
  const isMobile = useIsMobile();
  const busy = useRef(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // cardValueMapがnullの場合のフォールバック
  const effectiveCardValueMap = useMemo(() => {
    if (cardValueMap) return cardValueMap;
    if (allCards && allCards.length > 0) {
      return valueCalculator.calculateAllCardValues(allCards);
    }
    return new Map();
  }, [cardValueMap, allCards]);

  const [mode, setMode] = useState('intro'); // intro, running, completed
  const [currentDebt, setCurrentDebt] = useState(initialDebt);
  const [dailyResults, setDailyResults] = useState([]);
  const [currentDayIndex, setCurrentDayIndex] = useState(-1);
  const [isSkipping, setIsSkipping] = useState(false);
  const [surplus, setSurplus] = useState(0);

  // カード価格を取得（レアリティ倍率込み）
  const getCardValue = useCallback((card) => {
    const cardId = card.cardId || card.id;
    const cardValue = effectiveCardValueMap?.get?.(cardId);
    const baseValue = cardValue?.baseValue || 100;
    // レアリティ倍率を適用
    const rarityMultiplier = getRarityMultiplier(card.rarity);
    return Math.floor(baseValue * rarityMultiplier);
  }, [effectiveCardValueMap]);

  // 1日分の処理を実行
  const processOneDay = useCallback(async (currentPlayerData, debt) => {
    if (economyAccount()) {
      const result = await economyRequest('investment/recover', {});
      return { newPlayerData: applyEconomySnapshot(currentPlayerData, result.player, economyAccount()),
        dayResult: result, newDebt: result.remainingDebt, daySurplus: result.surplus };
    }
    // 1. 日付進行処理（市場更新、大会処理など）
    let newPlayerData = await processDayAdvancement(currentPlayerData);
    // 借金期限切れフラグは無視（既に差し押さえ済み）
    delete newPlayerData._seizureNeeded;
    delete newPlayerData._investmentResults;

    // 2. パック開封（常に勝利）
    const packResult = openPack(allCards, effectiveCardValueMap);

    // 3. 全カード即売却（レアリティ込み価格）
    let sellTotal = 0;
    const soldCards = packResult.map(packCard => {
      const cardValue = getCardValue(packCard);
      sellTotal += cardValue;
      return {
        cardId: packCard.cardId,
        rarity: packCard.rarity,
        name: packCard.card?.name || packCard.cardId,
        sellPrice: cardValue,
      };
    });

    // 4. 対戦報酬（5000G × 50% = 2500G を借金返済）
    const battleReward = SEIZURE_CONSTANTS.AUTO_BATTLE_REWARD || 5000;
    const debtRepaymentFromReward = Math.min(
      Math.floor(battleReward * (SEIZURE_CONSTANTS.AUTO_BATTLE_DEDUCTION_RATE || 0.5)),
      debt
    );

    // 5. カード売却分を借金返済に充当
    const debtAfterReward = debt - debtRepaymentFromReward;
    const repaymentFromCards = Math.min(sellTotal, debtAfterReward);
    const newDebt = Math.max(0, debtAfterReward - sellTotal);

    // 6. 余剰（完済した場合のみ）
    const daySurplus = newDebt === 0 ? Math.max(0, sellTotal - debtAfterReward) : 0;

    const dayResult = {
      day: (currentPlayerData.market?.currentDay || 1),
      battleReward,
      debtRepaymentFromReward,
      soldCards,
      sellTotal,
      repaymentFromCards,
      totalRepayment: debtRepaymentFromReward + repaymentFromCards,
      remainingDebt: newDebt,
      surplus: daySurplus,
      isComplete: newDebt === 0,
    };

    newPlayerData = { ...newPlayerData, investmentData: { ...newPlayerData.investmentData,
      debt: newDebt > 0 ? { ...newPlayerData.investmentData.debt, amount: newDebt } : null } };
    return { newPlayerData, dayResult, newDebt, daySurplus };
  }, [processDayAdvancement, allCards, getCardValue, effectiveCardValueMap]);

  // 1日進める（UI更新あり）
  const advanceOneDay = useCallback(async () => {
    if (currentDebt <= 0 || busy.current) return;
    busy.current = true; setProcessing(true); setError(null);
    try {

    const result = await processOneDay(playerData, currentDebt);

    // playerDataを更新（借金は別途管理）
    updatePlayerData(result.newPlayerData);

    // 日次結果を追加
    setDailyResults(prev => [...prev, result.dayResult]);
    setCurrentDayIndex(prev => prev + 1);
    setCurrentDebt(result.newDebt);

    if (result.dayResult.isComplete) {
      setSurplus(result.daySurplus);
    }

    return result;
    } catch (error) { setError(error.message); }
    finally { busy.current = false; setProcessing(false); }
  }, [currentDebt, playerData, processOneDay, updatePlayerData]);

  // スキップ処理
  const handleSkip = useCallback(() => {
    if (!busy.current) setIsSkipping(true);
  }, []);

  // スキップ中のループ処理
  useEffect(() => {
    if (!isSkipping || currentDebt <= 0 || busy.current) return;
    busy.current = true; setError(null);

    const runSkipLoop = async () => {
      let debt = currentDebt;
      let data = playerData;
      const results = [...dailyResults];
      let totalSurplus = 0;

      try {
      while (debt > 0 && results.length < 200) {
        const result = await processOneDay(data, debt);
        data = result.newPlayerData;
        debt = result.newDebt;
        results.push(result.dayResult);
        totalSurplus = result.daySurplus;

        // 200日上限（安全弁）
        if (results.length >= 200 && debt > 0) setError("200回に達しました。残債を保存して終了できます。");
      }

      } catch (error) { setError(error.message); }
      // 最終状態を更新
      updatePlayerData(data);
      setDailyResults(results);
      setCurrentDayIndex(results.length - 1);
      setCurrentDebt(debt);
      setSurplus(totalSurplus);
      setIsSkipping(false); busy.current = false;
    };

    runSkipLoop();
  }, [isSkipping, currentDebt, playerData, dailyResults, processOneDay, updatePlayerData]);

  // 借金完済チェック
  useEffect(() => {
    if (mode === 'running' && currentDebt <= 0) {
      setMode('completed');
    }
  }, [mode, currentDebt]);

  // 完了処理
  const handleComplete = useCallback(() => {
    if (currentDebt > 0) return;
    if (economyAccount()) {
      onComplete({ totalDays: dailyResults.length, startDebt: initialDebt, cappedSurplus: surplus, remainingDebt: 0 });
      return;
    }
    // 利益上限適用（10,000G）
    const profitCap = SEIZURE_CONSTANTS.PROFIT_CAP || 10000;
    const cappedSurplus = Math.min(surplus, profitCap);
    const confiscated = surplus - cappedSurplus;

    // playerDataの借金をクリア、余剰を所持金に加算
    updatePlayerData(prev => ({
      ...prev,
      gold: (prev.gold || 0) + cappedSurplus,
      investmentData: {
        ...prev.investmentData,
        debt: createInitialDebtState(),
      },
    }));

    onComplete({
      totalDays: dailyResults.length,
      startDebt: initialDebt,
      finalSurplus: surplus,
      cappedSurplus,
      confiscated,
      highRarityCards: dailyResults
        .flatMap(d => d.soldCards)
        .filter(c => ['GR', 'SP', 'ALT', 'SEC', 'HR', 'UR'].includes(c.rarity)),
    });
  }, [surplus, dailyResults, initialDebt, updatePlayerData, onComplete]);

  // 開始処理
  const handleStart = useCallback(() => {
    setMode('running');
    // 最初の1日を処理
    advanceOneDay();
  }, [advanceOneDay]);

  // 次の日へ
  const handleNextDay = useCallback(() => {
    if (currentDebt > 0) {
      advanceOneDay();
    }
  }, [currentDebt, advanceOneDay]);

  // スタイル
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      height: '100%',
      backgroundColor: '#1a1a2e',
      color: '#e0e0e0',
    },
    header: {
      padding: isMobile ? '12px' : '16px',
      backgroundColor: '#16213e',
      borderBottom: '2px solid #e94560',
      textAlign: 'center',
    },
    title: {
      fontSize: isMobile ? '18px' : '22px',
      fontWeight: 'bold',
      color: '#e94560',
    },
    content: {
      flex: 1,
      overflow: 'auto',
      padding: isMobile ? '12px' : '20px',
    },
    debtDisplay: {
      textAlign: 'center',
      padding: '16px',
      backgroundColor: 'rgba(233, 69, 96, 0.1)',
      borderRadius: '8px',
      marginBottom: '16px',
    },
    debtAmount: {
      fontSize: isMobile ? '28px' : '36px',
      fontWeight: 'bold',
      color: '#e94560',
    },
    debtLabel: {
      fontSize: '14px',
      color: '#888',
      marginTop: '4px',
    },
    dayCard: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '12px',
      border: '1px solid #333',
    },
    dayHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '12px',
      paddingBottom: '8px',
      borderBottom: '1px solid #333',
    },
    dayTitle: {
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#ffd700',
    },
    cardGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
      gap: '8px',
      marginBottom: '12px',
    },
    cardItem: {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      borderRadius: '4px',
      padding: '8px',
      textAlign: 'center',
      fontSize: '12px',
    },
    rarityBadge: {
      display: 'inline-block',
      padding: '2px 6px',
      borderRadius: '3px',
      fontSize: '10px',
      fontWeight: 'bold',
      marginBottom: '4px',
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '4px 0',
      fontSize: '14px',
    },
    actionBar: {
      padding: '16px',
      borderTop: '1px solid #333',
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
      display: 'flex',
      gap: '12px',
    },
    button: {
      flex: 1,
      padding: '14px',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 'bold',
    },
    primaryButton: {
      backgroundColor: '#e94560',
      color: '#fff',
    },
    secondaryButton: {
      backgroundColor: '#444',
      color: '#fff',
    },
    resultBox: {
      maxWidth: '500px',
      margin: '0 auto',
      textAlign: 'center',
    },
    resultTitle: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#4caf50',
      marginBottom: '24px',
    },
    resultSummary: {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
      textAlign: 'left',
    },
    resultRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: '1px solid #333',
    },
    highRarityList: {
      maxHeight: '150px',
      overflow: 'auto',
      marginBottom: '16px',
      textAlign: 'left',
    },
    confiscatedWarning: {
      padding: '12px',
      backgroundColor: 'rgba(255, 152, 0, 0.1)',
      border: '1px solid #ff9800',
      borderRadius: '8px',
      marginBottom: '16px',
      color: '#ff9800',
    },
  };

  // レアリティの色
  const getRarityColor = (rarity) => {
    const colors = {
      'GR': '#ffd700',
      'UR': '#ff6b6b',
      'SR': '#9d4ce6',
      'R': '#4fc3f7',
      'UC': '#4caf50',
      'C': '#888',
    };
    return colors[rarity] || '#888';
  };

  // イントロ画面
  if (mode === 'intro') {
    return (
      <div style={styles.container}>
        {error && <p role="alert">{error} <button onClick={() => onComplete({ remainingDebt: currentDebt, totalDays: dailyResults.length, cappedSurplus: surplus })}>残債を保存して戻る</button></p>}
        <div style={styles.header}>
          <div style={styles.title}>💀 自動対戦ループ</div>
        </div>
        <div style={styles.content}>
          <div style={styles.debtDisplay}>
            <div style={styles.debtAmount}>{initialDebt.toLocaleString()}G</div>
            <div style={styles.debtLabel}>残り借金</div>
          </div>

          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <p style={{ color: '#aaa', marginBottom: '16px' }}>
              カード売却だけでは借金を返済できませんでした。<br />
              自動対戦で返済を続けます。
            </p>
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'left',
              maxWidth: '400px',
              margin: '0 auto',
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>処理内容:</div>
              <ul style={{ margin: 0, paddingLeft: '20px', color: '#aaa' }}>
                <li>毎日自動で対戦（常に勝利）</li>
                <li>報酬5,000Gの50%を借金返済</li>
                <li>勝利パックを開封</li>
                <li>全カードを即売却→借金返済</li>
                <li>借金完済まで繰り返し</li>
              </ul>
            </div>
          </div>
        </div>
        <div style={styles.actionBar}>
          <button
            style={{ ...styles.button, ...styles.primaryButton }}
            onClick={handleStart}
          >
            開始
          </button>
        </div>
      </div>
    );
  }

  // ループ中画面
  if (mode === 'running') {
    const currentResult = dailyResults[currentDayIndex];

    return (
      <div style={styles.container}>
        {error && <p role="alert">{error} <button onClick={() => onComplete({ remainingDebt: currentDebt, totalDays: dailyResults.length, cappedSurplus: surplus })}>残債を保存して戻る</button></p>}
        <div style={styles.header}>
          <div style={styles.title}>
            📦 強制パック開封 - Day {dailyResults.length}
          </div>
        </div>
        <div style={styles.content}>
          <div style={styles.debtDisplay}>
            <div style={styles.debtAmount}>{currentDebt.toLocaleString()}G</div>
            <div style={styles.debtLabel}>残り借金</div>
          </div>

          {currentResult && (
            <div style={styles.dayCard}>
              <div style={styles.dayHeader}>
                <span style={styles.dayTitle}>Day {dailyResults.length}</span>
                <span style={{ color: '#4caf50' }}>
                  返済: {currentResult.totalRepayment.toLocaleString()}G
                </span>
              </div>

              <div style={{ marginBottom: '8px', fontSize: '13px', color: '#888' }}>
                開封カード:
              </div>
              <div style={styles.cardGrid}>
                {currentResult.soldCards.map((card, idx) => (
                  <div key={idx} style={styles.cardItem}>
                    <span style={{
                      ...styles.rarityBadge,
                      backgroundColor: getRarityColor(card.rarity),
                      color: card.rarity === 'C' || card.rarity === 'UC' ? '#fff' : '#000',
                    }}>
                      {card.rarity}
                    </span>
                    <div style={{ marginBottom: '2px', fontSize: '11px' }}>
                      {card.name || card.cardId}
                    </div>
                    <div style={{ color: '#ffd700' }}>
                      {card.sellPrice.toLocaleString()}G
                    </div>
                  </div>
                ))}
              </div>

              <div style={styles.summaryRow}>
                <span>対戦報酬からの返済</span>
                <span style={{ color: '#4caf50' }}>
                  {currentResult.debtRepaymentFromReward.toLocaleString()}G
                </span>
              </div>
              <div style={styles.summaryRow}>
                <span>カード売却からの返済</span>
                <span style={{ color: '#4caf50' }}>
                  {currentResult.repaymentFromCards.toLocaleString()}G
                </span>
              </div>
            </div>
          )}

          {isSkipping && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <div style={{ fontSize: '18px', marginBottom: '8px' }}>
                スキップ中...
              </div>
              <div style={{ color: '#888' }}>
                {dailyResults.length}日経過
              </div>
            </div>
          )}
        </div>
        <div style={styles.actionBar}>
          <button
            style={{ ...styles.button, ...styles.secondaryButton }}
            onClick={handleSkip}
            disabled={isSkipping || processing}
          >
            全部スキップ
          </button>
          <button
            style={{ ...styles.button, ...styles.primaryButton }}
            onClick={handleNextDay}
            disabled={isSkipping || processing || currentDebt <= 0}
          >
            次の日へ
          </button>
        </div>
      </div>
    );
  }

  // 完了画面
  if (mode === 'completed') {
    const profitCap = SEIZURE_CONSTANTS.PROFIT_CAP || 10000;
    const cappedSurplus = Math.min(surplus, profitCap);
    const confiscated = surplus - cappedSurplus;
    const highRarityCards = dailyResults
      .flatMap(d => d.soldCards)
      .filter(c => ['GR', 'SP', 'ALT', 'SEC', 'HR', 'UR'].includes(c.rarity));

    return (
      <div style={styles.container}>
        {error && <p role="alert">{error} <button onClick={() => onComplete({ remainingDebt: currentDebt, totalDays: dailyResults.length, cappedSurplus: surplus })}>残債を保存して戻る</button></p>}
        <div style={styles.header}>
          <div style={styles.title}>📊 自動返済完了</div>
        </div>
        <div style={styles.content}>
          <div style={styles.resultBox}>
            <div style={styles.resultTitle}>借金完済!</div>

            <div style={styles.resultSummary}>
              <div style={styles.resultRow}>
                <span>経過日数</span>
                <span>{dailyResults.length}日</span>
              </div>
              <div style={styles.resultRow}>
                <span>開封パック数</span>
                <span>{dailyResults.length}個</span>
              </div>
              <div style={styles.resultRow}>
                <span>開始時借金</span>
                <span style={{ color: '#e94560' }}>
                  {initialDebt.toLocaleString()}G
                </span>
              </div>
              <div style={styles.resultRow}>
                <span>余剰利益</span>
                <span style={{ color: '#4caf50' }}>
                  {cappedSurplus.toLocaleString()}G
                </span>
              </div>
            </div>

            {highRarityCards.length > 0 && (
              <>
                <div style={{ textAlign: 'left', fontWeight: 'bold', marginBottom: '8px' }}>
                  売却された高レアカード (UR以上):
                </div>
                <div style={styles.highRarityList}>
                  {highRarityCards.map((card, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '4px 8px',
                      backgroundColor: 'rgba(255, 255, 255, 0.02)',
                      borderRadius: '4px',
                      marginBottom: '4px',
                    }}>
                      <span>
                        <span style={{ color: getRarityColor(card.rarity) }}>
                          [{card.rarity}]
                        </span>
                        {' '}{card.name || card.cardId}
                      </span>
                      <span style={{ color: '#ffd700' }}>
                        {card.sellPrice.toLocaleString()}G
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {confiscated > 0 && (
              <div style={styles.confiscatedWarning}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  ⚠️ 利益上限超過
                </div>
                <div style={{ fontSize: '14px' }}>
                  余剰利益 {surplus.toLocaleString()}G のうち、
                  {confiscated.toLocaleString()}G が没収されました。
                </div>
                <div style={{ fontSize: '12px', marginTop: '4px', color: '#aaa' }}>
                  「差し押さえ先がちょろまかしたようだ...」
                </div>
              </div>
            )}

            <button
              style={{ ...styles.button, ...styles.primaryButton, width: '100%' }}
              onClick={handleComplete}
            >
              タイトルへ
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AutoBattleLoop;
